import { useCallback, useEffect, useRef, useState } from 'react'

import { FaqDataClient, useFaqData } from '../contexts/FaqDataContext'

type DataClientMethod<T, R> = (params: T) => Promise<R>

/**
 * FaqDataClientのメソッドを呼び出すためのカスタムフック
 * useQueryのような挙動を提供する
 */
function useFaqDataQuery<T, R>(
  method: (client: FaqDataClient) => DataClientMethod<T, R>,
  params: T,
  skip = false
) {
  const client = useFaqData()
  const [loading, setLoading] = useState(!skip)
  const [data, setData] = useState<R | null>(null)
  const [error, setError] = useState<Error | null>(null)

  // methodをrefで保持し、インライン関数でも無限ループを防ぐ
  const methodRef = useRef(method)
  methodRef.current = method

  useEffect(() => {
    if (skip) return

    let isMounted = true
    setLoading(true)

    methodRef
      .current(client)(params)
      .then(result => {
        if (isMounted) {
          setData(result)
          setLoading(false)
        }
      })
      .catch(err => {
        if (isMounted) {
          setError(err)
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
    // clientを依存配列に含め、Provider差し替え時に再フェッチする
    // methodはrefで保持しているため依存配列には含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, skip, JSON.stringify(params)])

  return { loading, data, error }
}

/**
 * FaqDataClientのメソッドを遅延実行するためのカスタムフック
 * useLazyQueryのような挙動を提供する
 */
function useFaqDataLazyQuery<T, R>(method: (client: FaqDataClient) => DataClientMethod<T, R>) {
  const client = useFaqData()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<R | null>(null)
  const [error, setError] = useState<Error | null>(null)

  // methodをrefで保持し、インライン関数でも無限ループを防ぐ
  const methodRef = useRef(method)
  methodRef.current = method

  const execute = useCallback(
    async (params: T) => {
      setLoading(true)
      try {
        const result = await methodRef.current(client)(params)
        setData(result)
        setLoading(false)
        return result
      } catch (err) {
        setError(err as Error)
        setLoading(false)
        throw err
      }
    },
    [client]
  )

  return [execute, { loading, data, error }] as const
}

// よくあるご質問（閲覧数上位）を取得するフック
export function useTopPageViewFAQs(params: {
  botId: string
  tenantId?: string | null
  skip?: boolean
}) {
  const { skip, ...queryParams } = params
  return useFaqDataQuery(client => client.fetchTopPageViewFAQs, queryParams, skip)
}

// カテゴリに紐づくFAQ一覧を取得するフック
export function useCategoryFaq(params: {
  botId: string
  categories: string[]
  tenantId?: string | null
  skip?: boolean
}) {
  const { skip, ...queryParams } = params
  return useFaqDataQuery(client => client.fetchCategoryFaq, queryParams, skip)
}

// FAQ検索を実行するフック
export function useSearchFaq(params: {
  botId: string
  inputValue: string
  searchType?: string | null
  tenantId?: string | null
  count?: number
}) {
  return useFaqDataQuery(client => client.searchFaq, params)
}

// FAQ検索（遅延実行）
export function useSearchFaqLazy() {
  return useFaqDataLazyQuery(client => client.searchFaq)
}

// フィルター検索を実行するフック
export function useFilterSearch(params: {
  botId: string
  q: string[]
  page: number
  tags: string[]
  searchType?: string
  queryThreshold?: number
  tenantId?: string | null
}) {
  return useFaqDataQuery(client => client.filterSearch, params)
}

// フィルター検索（遅延実行）
export function useFilterSearchLazy() {
  return useFaqDataLazyQuery(client => client.filterSearch)
}

// オートコンプリート候補を取得するフック（遅延実行）
export function useCompleteLazy() {
  return useFaqDataLazyQuery(client => client.fetchComplete)
}

// サジェストキーワードを取得するフック（遅延実行）
export function useSuggestLazy() {
  return useFaqDataLazyQuery(client => client.fetchSuggest)
}

// プルダウンクエリを実行するフック（遅延実行）
export function usePulldownQueryLazy() {
  return useFaqDataLazyQuery(client => client.fetchPulldownQuery)
}

// スキルトークンを取得するフック（遅延実行）
export function useSkillTokenLazy() {
  return useFaqDataLazyQuery(client => client.fetchSkillToken)
}
